import { createSlice, createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AppDispatch, ThunkAPI } from '@frameworks/redux';

import { getEthersProvider } from '@frameworks/ethers';
import { WalletState, Subscriptions, Theme } from '@types';
import { toChecksumAddress } from '@utils';

const walletChange = createAction<{ walletName: string }>(
  'wallet/walletChange'
);
const addressChange = createAction<{ address: string }>('wallet/addressChange');
const networkChange = createAction<{ network: number }>('wallet/networkChange');
const balanceChange = createAction<{ balance: string }>('wallet/balanceChange');

const getSubscriptions = (
  dispatch: AppDispatch,
  customSubscriptions?: Subscriptions
) => ({
  wallet: (wallet: any) => {
    dispatch(walletChange({ walletName: wallet.name }));
    if (customSubscriptions && customSubscriptions.wallet)
      customSubscriptions.wallet(wallet);
  },
  address: (address: string) => {
    dispatch(addressChange({ address }));
    if (customSubscriptions && customSubscriptions.address)
      customSubscriptions.address(address);
  },
  network: (network: number) => {
    dispatch(networkChange({ network }));
    if (customSubscriptions && customSubscriptions.network)
      customSubscriptions.network(network);
  },
  balance: (balance: string) => {
    dispatch(balanceChange({ balance }));
    if (customSubscriptions && customSubscriptions.balance)
      customSubscriptions.balance(balance);
  },
});

const walletSelect = createAsyncThunk<
  { isConnected: boolean },
  { walletName?: string },
  ThunkAPI
>(
  'wallet/walletSelect',
  async ({ walletName }, { dispatch, getState, extra }) => {
    const { context, config } = extra;
    const { wallet, web3Provider } = context;
    const { NETWORK, ETHEREUM_PROVIDER } = config;
    const { settings } = getState();

    if (!wallet.isCreated) {
      const customSubscriptions: Subscriptions = {
        wallet: (wallet) => {
          web3Provider.register('wallet', getEthersProvider(wallet.provider));
        },
      };
      const subscriptions = getSubscriptions(dispatch, customSubscriptions);
      // TODO: Evaluate if all wallets work with ETHEREUM_PROVIDER or if should change
      wallet.create(NETWORK, subscriptions, ETHEREUM_PROVIDER, settings.theme);
    }
    const isConnected = await wallet.connect({ name: walletName });
    return { isConnected };
  }
);

const changeWalletTheme = createAsyncThunk<void, { theme: Theme }, ThunkAPI>(
  'wallet/walletSelect',
  async ({ theme }, { extra }) => {
    const { context } = extra;
    const { wallet } = context;
    if (wallet.isCreated && wallet.changeTheme) {
      wallet.changeTheme(theme);
    }
  }
);

export const walletInitialState: WalletState = {
  selectedAddress: undefined,
  networkVersion: undefined,
  balance: undefined,
  name: undefined,
  isConnected: false,
  isLoading: false,
  error: undefined,
};

const walletSlice = createSlice({
  name: 'settings',
  initialState: walletInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(walletChange, (state, { payload: { walletName } }) => {
        state.name = walletName;
      })
      .addCase(addressChange, (state, { payload: { address } }) => {
        state.selectedAddress = address
          ? toChecksumAddress(address)
          : undefined;
      })
      .addCase(networkChange, (state, { payload: { network } }) => {
        state.networkVersion = network;
      })
      .addCase(balanceChange, (state, { payload: { balance } }) => {
        state.balance = balance;
      })
      .addCase(walletSelect.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(
        walletSelect.fulfilled,
        (state, { payload: { isConnected } }) => {
          state.isConnected = isConnected;
          state.isLoading = false;
        }
      )
      .addCase(walletSelect.rejected, (state, { error }) => {
        state.isLoading = false;
        state.error = error.message;
      });
  },
});

export const WalletActions = {
  ...walletSlice.actions,
  walletChange,
  addressChange,
  networkChange,
  balanceChange,
  walletSelect,
  changeWalletTheme,
};
export default walletSlice.reducer;
